import { LogLevelDesc } from "loglevel";
import type {
    CbmSdkClientOptions,
    AddVoiceParticipantOptions,
    AddVoiceParticipantResponse,
    LeaveChannelResponse,
    AddEmailParticipantOptions,
    CreateEmailTaskOptions,
    CreateCbmTaskResponse,
    PausedChannelsResponse,
    Transfer,
    Cbm,
    CbmChannel,
    RemoveParticipantOptions,
    VoiceParticipantModification
} from "./Cbm";
import { CbmSdk, ChannelStatus, LogLevel } from "./Cbm";
import { ClientEventEmitter } from "~/modules/client/ClientEventEmitter/ClientEventEmitter";
import { Reservation, Task, RejectOptions } from "~/packages/taskrouter";

import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import type { Session } from "~/modules/session/Session/Session";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";

import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { ClientEvent } from "~/modules/client/Client/ClientEvent";
import { getConfigRegion } from "~/utils/regionUtil";
import { cbmParticipantToTaskParticipant, TaskParticipant, toVoiceMediaProperties } from "~/modules/participant";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

export class CbmImpl implements Cbm {
    readonly #logger: Logger;

    readonly #session: Session;

    readonly #clientOptions: ClientOptionsStore;

    #isInitialized: boolean;

    #cbm: CbmSdk;

    #eventListenersRegistered: boolean = false;

    readonly #ctx: ContextManager;

    constructor(ctx: ContextManager) {
        this.#ctx = ctx;
        this.#session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.#logger = getLogger(ctx)(LoggerName.CBM);
    }

    async init(cbmInstance?: CbmSdk): Promise<void> {
        if (this.#isInitialized) {
            return Promise.resolve();
        }
        this.#registerEventListeners();
        if (cbmInstance) {
            this.#cbm = cbmInstance;
            this.#isInitialized = true;
            return Promise.resolve();
        }

        const configRegion = getConfigRegion(this.#clientOptions);

        const cbmOptions: CbmSdkClientOptions = {
            logLevel: this.mapToLogLevel(this.#clientOptions?.logger?.level)
        };

        if (configRegion !== undefined) {
            cbmOptions.region = configRegion;
        }

        this.#cbm = new CbmSdk(this.#session.token, cbmOptions);
        this.#isInitialized = true;
        return Promise.resolve();
    }

    updateToken(token: string): void {
        if (!this.#cbm) {
            this.#logger.warn("updateToken in CbmImpl, but there is no CbmSdk instance");
        }

        this.#cbm.updateToken(token);
    }

    async acceptReservation(reservation: Reservation): Promise<Reservation> {
        

        
        return this.#cbm.acceptReservation(reservation);
    }

    async addVoiceParticipant(task: Task, options: AddVoiceParticipantOptions): Promise<AddVoiceParticipantResponse> {
        

        
        const { pendingParticipantResponse, waitForParticipantToSettle } = await this.#cbm.addVoiceParticipant(
            task,
            options
        );

        return {
            pendingParticipantResponse: {
                channelSid: pendingParticipantResponse.channel_sid,
                interactionSid: pendingParticipantResponse.interaction_sid,
                sid: pendingParticipantResponse.sid,
                mediaProperties: pendingParticipantResponse.media_properties
                    ? toVoiceMediaProperties(pendingParticipantResponse.media_properties)
                    : null
            },
            waitForParticipantToSettle: new Promise<TaskParticipant>((resolve, reject) => {
                waitForParticipantToSettle
                    
                    .then((cbmParticipant) => {
                        return resolve(cbmParticipantToTaskParticipant(cbmParticipant));
                    })
                    
                    .catch(reject);
            })
        };
    }

    async removeVoiceParticipant(task: Task, participantSid: string): Promise<TaskParticipant> {
        

        
        const cbmParticipant = await this.#cbm.removeVoiceParticipant(task, participantSid);
        return cbmParticipantToTaskParticipant(cbmParticipant);
    }

    async endConference(reservation: Reservation): Promise<Reservation> {
        

        
        await this.#cbm.endConference(reservation.task);
        return reservation;
    }

    async rejectReservation(reservation: Reservation, rejectOptions?: RejectOptions): Promise<Reservation> {
        

        
        return this.#cbm.rejectReservation(reservation, rejectOptions);
    }

    async holdParticipant(task: Task, participantSid: string): Promise<TaskParticipant> {
        const cbmParticipant = await this.#cbm.holdParticipant(task, participantSid);
        return cbmParticipantToTaskParticipant(cbmParticipant);
    }

    async unholdParticipant(task: Task, participantSid: string): Promise<TaskParticipant> {
        const cbmParticipant = await this.#cbm.unHoldParticipant(task, participantSid);
        return cbmParticipantToTaskParticipant(cbmParticipant);
    }

    async modifyVoiceParticipant(
        task: Task,
        participantSid: string,
        modification: VoiceParticipantModification
    ): Promise<TaskParticipant> {
        const cbmParticipant = await this.#cbm.modifyVoiceParticipant(task, participantSid, modification);
        return cbmParticipantToTaskParticipant(cbmParticipant);
    }

    async getParticipantsByTask(task: Task, options?: object): Promise<TaskParticipant[]> {
        const channels = await this.#cbm.getChannels(task);
        const participants = await this.#cbm.getParticipants(task, channels[0].sid, options);
        return participants.map((participant) => cbmParticipantToTaskParticipant(participant));
    }

    async getParticipantBySid(sid: string, task: Task): Promise<TaskParticipant | undefined> {
        const channels = await this.#cbm.getChannels(task);

        const activeChannel = channels.find((channel) => channel.status === ChannelStatus.Active);
        if (!activeChannel?.sid) {
            const errorMsg = `getParticipantBySid: Active channel for task ${task.sid} not found`;
            this.#logger.error(errorMsg);
            return throwFlexSdkError(this.#ctx)(
                ErrorCode.ActiveChannelNotFound,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }
        const participants = await this.#cbm.getParticipants(task, activeChannel.sid);

        const participant = participants.find((p) => p.participantSid === sid);

        if (!participant) {
            const warnMsg = `Participant with sid: ${sid} not found`;
            this.#logger.warn(warnMsg);
            return participant;
        }

        return cbmParticipantToTaskParticipant(participant);
    }

    async completeReservation(reservation: Reservation): Promise<Reservation> {
        

        
        return this.#cbm.completeReservation(reservation);
    }

    wrapReservation(reservation: Reservation): Promise<Reservation> {
        

        
        return this.#cbm.wrapReservation(reservation);
    }

    async leaveChannel(
        interactionSid: string,
        channelSid: string,
        channelStatus: string,
        taskStatus: string
    ): Promise<LeaveChannelResponse> {
        

        
        return this.#cbm.leaveChannel(interactionSid, channelSid, channelStatus, taskStatus);
    }

    async pauseChannel(
        interactionSid: string,
        channelSid: string,
        taskStatus: string,
        routingStatus: string
    ): Promise<string> {
        
        const response = await this.#cbm.pauseChannel(interactionSid, channelSid, taskStatus, routingStatus);

        return response?.sid;
    }

    async resumeChannel(interactionSid: string, channelSid: string, taskStatus: string): Promise<string> {
        
        const response = await this.#cbm.resumeChannel(interactionSid, channelSid, taskStatus);
        return response?.sid;
    }

    async addEmailParticipant(task: Task, options: AddEmailParticipantOptions): Promise<TaskParticipant> {
        

        
        const cbmParticipant = await this.#cbm.addEmailParticipant(task, options);
        return cbmParticipantToTaskParticipant(cbmParticipant);
    }

    async transferChannel(
        flexInstanceSid: string,
        interactionSid: string,
        channelSid: string,
        from: string,
        to: string
    ): Promise<string> {
        
        const response = await this.#cbm.transferChannel(flexInstanceSid, interactionSid, channelSid, from, to);
        return response?.sid;
    }

    async createEmailTask(
        worker: TaskRouter.Worker,
        workflowSid: string,
        taskQueueSid: string,
        options: CreateEmailTaskOptions
    ): Promise<CreateCbmTaskResponse> {
        
        return this.#cbm.createEmailTask(worker, workflowSid, taskQueueSid, options);
    }

    async removeEmailParticipant(task: Task, participantSid: string): Promise<TaskParticipant> {
        const cbmParticipant = await this.#cbm.removeParticipant(task, participantSid);
        return cbmParticipantToTaskParticipant(cbmParticipant);
    }

    async removeParticipant(
        task: Task,
        participantSid: string,
        removeParticipantOptions: RemoveParticipantOptions
    ): Promise<TaskParticipant> {
        
        
        const cbmParticipant = await this.#cbm.removeParticipant(task, participantSid, removeParticipantOptions);
        return cbmParticipantToTaskParticipant(cbmParticipant);
    }

    async getPausedChannels(pageSize: number, pageToken?: string): Promise<PausedChannelsResponse> {
        
        
        return this.#cbm.getPausedChannels(pageSize, pageToken);
    }

    async getChannels(task: Task): Promise<CbmChannel[]> {
        
        
        return this.#cbm.getChannels(task);
    }

    private mapToLogLevel(logLevel: LogLevelDesc): LogLevel {
        if (typeof logLevel === "number") {
            switch (logLevel) {
                case 0:
                    return LogLevel.Trace;
                case 1:
                    return LogLevel.Debug;
                case 2:
                    return LogLevel.Info;
                case 3:
                    return LogLevel.Warn;
                case 4:
                    return LogLevel.Error;
                case 5:
                    return LogLevel.Silent;
                default:
                    return LogLevel.Warn;
            }
        } else if (typeof logLevel === "string") {
            const lowercaseLogLevel = logLevel.toLowerCase() as LogLevel;
            if (Object.values(LogLevel).includes(lowercaseLogLevel)) {
                return lowercaseLogLevel;
            }
        }
        return LogLevel.Warn;
    }

    getChannelTransfers(
        flexInstanceSid: string,
        interactionSid: string,
        channelSid: string
    ): Promise<Array<Transfer>> | undefined {
        return this.#cbm.getChannelTransfers(flexInstanceSid, interactionSid, channelSid);
    }

    


    #registerEventListeners(): void {
        if (this.#eventListenersRegistered) return;
        this.#logger.debug("Cbm: Registering client event listeners");

        const clientEventEmitter = this.#ctx.retrieveInstanceOf(ContextInstance.ClientEventEmitter, ClientEventEmitter);

        clientEventEmitter.addListener(ClientEvent.TokenUpdated, (token: string) => this.updateToken(token));
        this.#eventListenersRegistered = true;
    }
}
